/**
 * Small, dependency-free charts.
 *
 * Inline SVG rather than a charting library: the app is bundled and served
 * from its own origin, so a CDN script is not available to it, and pulling a
 * library in for two bar charts would cost more than it returns.
 *
 * Every chart here prints the actual value beside the bar. A bar alone
 * invites the reader to estimate, and the whole point of this product is that
 * the figures are exact and traceable — a chart that obscures them argues
 * against itself.
 */

export interface BarDatum {
  label: string;
  value: number;
  /** Optional second line under the label. */
  sub?: string;
  /** Overrides the default colour — used to mark a value as unusual. */
  tone?: "normal" | "warn" | "bad" | "good";
}

const TONE: Record<string, string> = {
  normal: "var(--accent, #4f46e5)",
  good: "#15803d",
  warn: "#b45309",
  bad: "#b91c1c",
};

function fmt(n: number): string {
  return n.toLocaleString("en-US", { maximumFractionDigits: 2 });
}

export function BarChart({
  data,
  title,
  unit,
  testId,
}: {
  data: BarDatum[];
  title?: string;
  unit?: string;
  testId?: string;
}) {
  if (data.length === 0) return null;
  // Scaled against the largest absolute value so a negative bar is readable
  // beside a positive one.
  const max = Math.max(...data.map((d) => Math.abs(d.value)), 1);

  return (
    <div data-testid={testId} style={{ marginTop: 6 }}>
      {title ? <div className="section-eyebrow">{title}</div> : null}
      <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 6 }}>
        {data.map((d) => {
          const pct = (Math.abs(d.value) / max) * 100;
          return (
            <div
              key={d.label}
              style={{ display: "grid", gridTemplateColumns: "150px 1fr auto", gap: 10, alignItems: "center" }}
            >
              <div style={{ fontSize: 12, lineHeight: 1.25 }}>
                <div style={{ fontWeight: 600 }}>{d.label}</div>
                {d.sub ? (
                  <div style={{ opacity: 0.6, fontSize: 11 }}>{d.sub}</div>
                ) : null}
              </div>
              <div
                style={{
                  height: 10,
                  borderRadius: 5,
                  background: "var(--bg-soft, #f1f1f4)",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    width: `${pct}%`,
                    height: "100%",
                    borderRadius: 5,
                    background: TONE[d.tone ?? "normal"],
                    transition: "width .3s ease",
                  }}
                />
              </div>
              {/* The number itself, always. A bar invites estimation; this
                  product's claim is that the figure is exact. */}
              <div style={{ fontSize: 12, fontVariantNumeric: "tabular-nums", minWidth: 90, textAlign: "right" }}>
                {fmt(d.value)}
                {unit ? <span style={{ opacity: 0.6 }}> {unit}</span> : null}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * A verdict mix: how a Hand's runs came out. The segments are never summed
 * into a single "success" figure — an escalation is the agent working, and
 * adding it to failures would report a healthy system as a broken one.
 */
export function VerdictBar({
  autoCompleted,
  toPerson,
  failed,
}: {
  autoCompleted: number;
  toPerson: number;
  failed: number;
}) {
  const total = autoCompleted + toPerson + failed;
  if (total === 0) return null;
  const seg = (n: number, colour: string, label: string) =>
    n === 0 ? null : (
      <div
        key={label}
        title={`${label}: ${n}`}
        style={{ width: `${(n / total) * 100}%`, background: colour, height: "100%" }}
      />
    );
  return (
    <div style={{ marginTop: 8 }}>
      <div
        style={{
          display: "flex",
          height: 8,
          borderRadius: 4,
          overflow: "hidden",
          background: "var(--bg-soft, #f1f1f4)",
        }}
      >
        {seg(autoCompleted, "#15803d", "Unattended")}
        {seg(toPerson, "#b45309", "To a person")}
        {seg(failed, "#b91c1c", "Failed")}
      </div>
      <div style={{ display: "flex", gap: 12, fontSize: 11, opacity: 0.75, marginTop: 5 }}>
        {autoCompleted > 0 ? <span>● {autoCompleted} unattended</span> : null}
        {toPerson > 0 ? <span>● {toPerson} to a person</span> : null}
        {failed > 0 ? <span>● {failed} failed</span> : null}
      </div>
    </div>
  );
}
